import React, { useRef, useState, useEffect } from "react";
import Footer from "../Footer/Footer.tsx";
import Navbar from "../NavBar/NavBar.tsx";
import { useHistory } from "react-router-dom";

/* Colours: text #4e5356 | buttons #2596be | headings #4573a6
   Fonts  : Ubuntu Bold (headings) / Open Sans Regular (body) */

const features = [
  { title: "Flexible Engagements", text: "Hourly, short-term and long-term assignments based on your business requirements.", icon: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></> },
  { title: "Skilled & Certified Developers", text: "Industry-proven developers, proficient in modern frameworks, tools and emerging technologies.", icon: <path d="M16 18l6-6-6-6M8 6l-6 6 6 6" /> },
  { title: "Verified Quality Control", text: "Every deliverable is reviewed by a Team Lead or Senior Developer before it reaches you.", icon: <><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z" /><path d="M9 12l2 2 4-4" /></> },
  { title: "Guaranteed Continuity", text: "If a developer becomes unavailable, a qualified backup steps in immediately, ensuring zero downtime.", icon: <path d="M21 12a9 9 0 11-3-6.7L21 8M21 3v5h-5" /> },
  { title: "Timely Delivery, Every Time", text: "We are committed to hitting your deadlines, no matter how tight the timeline.", icon: <path d="M13 2L3 14h9l-1 8 10-12h-9z" /> },
  { title: "Affordable Excellence", text: "World-class solutions at a competitive and transparent pricing model.", icon: <path d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" /> },
];

const models = [
  { name: "Hourly", note: "Pay only for the time you need" },
  { name: "Short Term", note: "Fast support for sprints and launches" },
  { name: "Long Term", note: "Dedicated experts as part of your team" },
];

const STYLES = `
@import url("https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600&family=Ubuntu:wght@700&display=swap");

.sa{--text:#4e5356;--head:#4573a6;--btn:#2596be;--tint:#eef6fa;--line:#d3e4ed;
  position:relative;overflow:hidden;font-family:"Open Sans",sans-serif;font-weight:400;color:var(--text);
  background:#fff}
.sa>*{position:relative}

.sa-wrap{max-width:1240px;margin:0 auto;padding:0 24px}

/* ---------- HERO ---------- */
.sa-hero{display:flex;align-items:center;justify-content:space-between;gap:60px;padding:90px 0 80px}
.sa-hero-text{flex:1;min-width:0;max-width:600px;animation:saL .9s cubic-bezier(.2,.8,.2,1) backwards}
.sa-chip{display:inline-block;margin-bottom:16px;padding:6px 18px;border-radius:50px;background:#fff;border:1px solid var(--line);
  color:var(--head);font-size:12px;letter-spacing:2px;text-transform:uppercase}
.sa h1{margin:0 0 18px;font-family:"Ubuntu",sans-serif;font-weight:700;font-size:42px;line-height:1.15;color:var(--head);cursor:default;transition:color .35s,letter-spacing .35s}
.sa h1:hover{color:var(--btn);letter-spacing:.6px}
.sa-line{display:flex;align-items:center;gap:12px;margin-bottom:20px}
.sa-line i{display:block;height:2px;width:40px;border-radius:2px;background:var(--btn)}.sa-line i:last-child{background:var(--head)}
.sa-desc{margin:0 0 28px;font-size:14.5px;line-height:1.75;color:var(--text);max-width:540px}
.sa-desc b{font-family:"Ubuntu",sans-serif;font-weight:700;color:var(--head)}

/* button (same as .op-btn) */
.sa-btn{position:relative;overflow:hidden;z-index:0;display:inline-flex;align-items:center;gap:8px;padding:12px 28px;border:none;border-radius:50px;
  background:var(--tint);color:var(--head);font-family:"Open Sans",sans-serif;font-weight:600;font-size:14px;cursor:pointer;
  box-shadow:0 0 0 1px var(--line) inset;transition:color .35s,transform .35s cubic-bezier(.3,1.6,.5,1),box-shadow .35s}
.sa-btn::before{content:"";position:absolute;inset:0;z-index:-1;background:linear-gradient(90deg,var(--head),var(--btn));transform:translateX(-101%);transition:transform .45s cubic-bezier(.2,.8,.2,1)}
.sa-btn:hover{color:#fff;transform:translateY(-3px);box-shadow:0 16px 26px -12px rgba(37,150,190,.7)}
.sa-btn:hover::before{transform:none}
.sa-btn span{display:inline-block;transition:transform .35s}
.sa-btn:hover span{transform:translateX(5px)}
.sa-btn.solid{background:#fff;color:var(--head)}

/* engagement panel */
.sa-panel{position:relative;flex:none;width:400px;animation:saR .9s .15s cubic-bezier(.2,.8,.2,1) backwards}
.sa-panel-box{position:relative;padding:34px 28px 28px;border-radius:28px;background:#fff;border:1px solid var(--line);box-shadow:0 30px 50px -28px rgba(69,115,166,.6);overflow:hidden}
.sa-panel-box::before{content:"";position:absolute;top:0;left:0;width:100%;height:5px;background:linear-gradient(90deg,var(--head),var(--btn))}
.sa-panel h3{margin:0 0 18px;font-family:"Ubuntu",sans-serif;font-weight:700;font-size:20px;letter-spacing:.6px;color:var(--head)}
.sa-model{display:flex;align-items:center;gap:14px;padding:14px 16px;margin-bottom:12px;border-radius:18px;background:var(--tint);border:1px solid transparent;cursor:default;
  transition:transform .35s cubic-bezier(.3,1.4,.5,1),background .35s,border-color .35s,box-shadow .35s}
.sa-model:last-child{margin-bottom:0}
.sa-model:hover{transform:translateX(8px);background:#fff;border-color:rgba(37,150,190,.5);box-shadow:0 14px 26px -16px rgba(37,150,190,.7)}
.sa-model em{flex:none;width:36px;height:36px;border-radius:12px;display:flex;align-items:center;justify-content:center;font-style:normal;
  font-family:"Ubuntu",sans-serif;font-weight:700;font-size:14px;color:#fff;background:linear-gradient(135deg,var(--head),var(--btn))}
.sa-model b{display:block;font-family:"Ubuntu",sans-serif;font-weight:700;font-size:16px;letter-spacing:.4px;color:var(--head)}
.sa-model span{font-size:13px;color:var(--text)}
.sa-float{position:absolute;right:-18px;bottom:-26px;display:flex;align-items:center;gap:10px;padding:10px 16px;border-radius:16px;background:rgba(255,255,255,.96);
  border:1px solid var(--line);box-shadow:0 18px 30px -16px rgba(69,115,166,.55);font-size:13px;animation:saFloat 5s ease-in-out infinite}
.sa-float b{display:block;font-family:"Ubuntu",sans-serif;font-weight:700;font-size:15px;color:var(--head)}
.sa-float .dot{width:34px;height:34px;border-radius:10px;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;background:linear-gradient(135deg,var(--head),var(--btn))}

/* ---------- FEATURES ---------- */
.sa-sec{text-align:center;padding:30px 0 100px}
.sa-sec h2{margin:0 0 14px;font-family:"Ubuntu",sans-serif;font-weight:700;font-size:42px;line-height:1.15;color:var(--head);cursor:default;transition:color .35s,letter-spacing .35s}
.sa-sec h2:hover{color:var(--btn);letter-spacing:.6px}
.sa-sub{display:flex;align-items:center;justify-content:center;gap:12px;margin-bottom:56px}
.sa-sub i{display:block;height:2px;width:40px;border-radius:2px;background:var(--btn)}.sa-sub i:last-child{background:var(--head)}
.sa-sub p{margin:0;font-size:15px}

.sa-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:22px}
.sa-grid:not(.in) .sa-card{opacity:0}
.sa-grid.in .sa-card{animation:saRise .8s cubic-bezier(.2,.8,.2,1) backwards;animation-delay:calc(var(--i) * .1s)}

.sa-card{--mx:50%;--my:50%;--rx:0deg;--ry:0deg;position:relative;overflow:hidden;text-align:center;padding:38px 24px 32px;border-radius:24px;
  background:#fff;border:1px solid var(--line);box-shadow:0 18px 36px -26px rgba(69,115,166,.55);
  transform:perspective(900px) rotateX(var(--rx)) rotateY(var(--ry));transition:transform .18s ease-out,box-shadow .4s,border-color .4s}
.sa-card:hover{border-color:transparent;box-shadow:0 30px 50px -22px rgba(37,150,190,.7)}
.sa-glow{position:absolute;inset:0;pointer-events:none;opacity:0;transition:opacity .35s;
  background:radial-gradient(240px circle at var(--mx) var(--my),rgba(37,150,190,.16),rgba(69,115,166,.05) 45%,transparent 70%)}
.sa-card:hover .sa-glow{opacity:1}
.sa-top{position:absolute;top:0;left:0;width:100%;height:5px;transform:scaleX(0);transform-origin:left;background:linear-gradient(90deg,var(--head),var(--btn));transition:transform .55s cubic-bezier(.2,.8,.2,1)}
.sa-card:hover .sa-top{transform:scaleX(1)}
.sa-num{position:absolute;top:10px;right:14px;font-size:54px;font-weight:800;line-height:1;pointer-events:none;color:transparent;-webkit-text-stroke:1.5px rgba(69,115,166,.14);
  transition:transform .5s cubic-bezier(.2,.8,.2,1),-webkit-text-stroke-color .4s}
.sa-card:hover .sa-num{transform:translate(4px,-4px);-webkit-text-stroke-color:rgba(37,150,190,.4)}
.sa-icon{position:relative;width:84px;height:84px;margin:0 auto 22px;border-radius:24px;background:var(--tint);display:flex;align-items:center;justify-content:center;
  transition:background .4s,box-shadow .4s,transform .5s cubic-bezier(.3,1.4,.5,1)}
.sa-icon svg{width:36px;height:36px;fill:none;stroke:var(--head);stroke-width:1.8;stroke-linecap:round;stroke-linejoin:round;transition:stroke .4s,transform .5s cubic-bezier(.3,1.4,.5,1)}
.sa-card:hover .sa-icon{background:linear-gradient(135deg,var(--head),var(--btn));transform:translateY(-6px);box-shadow:0 10px 22px -8px rgba(37,150,190,.6)}
.sa-card:hover .sa-icon svg{stroke:#fff;transform:scale(1.1)}
.sa-card h3{position:relative;display:inline-block;margin:0 0 12px;font-family:"Ubuntu",sans-serif;font-weight:700;font-size:20px;letter-spacing:.6px;color:var(--head)}
.sa-card h3::after{content:"";position:absolute;left:0;bottom:-5px;height:2px;width:0;background:var(--btn);transition:width .45s cubic-bezier(.2,.8,.2,1)}
.sa-card:hover h3::after{width:100%}
.sa-card p{position:relative;margin:0;font-size:14.5px;line-height:1.75;color:var(--text)}

/* ---------- CTA ---------- */
.sa-cta{position:relative;overflow:hidden;display:flex;align-items:center;justify-content:space-between;gap:24px;flex-wrap:wrap;text-align:left;
  padding:40px 44px;margin-bottom:100px;border-radius:28px;color:#fff;background:linear-gradient(120deg,var(--head),var(--btn));box-shadow:0 30px 60px -34px rgba(37,150,190,.8)}
.sa-cta::after{content:"";position:absolute;width:280px;height:280px;border-radius:50%;right:-80px;top:-130px;background:rgba(255,255,255,.18);filter:blur(40px);pointer-events:none}
.sa-cta>*{position:relative;z-index:1}
.sa-cta h3{margin:0 0 8px;font-family:"Ubuntu",sans-serif;font-weight:700;font-size:28px;letter-spacing:.6px;line-height:1.25;color:#fff}
.sa-cta p{margin:0;max-width:640px;font-size:14.5px;line-height:1.75;color:rgba(255,255,255,.9)}
.sa-cta p b{font-weight:600;color:#fff}

@keyframes saL{from{opacity:0;transform:translateX(-70px)}}
@keyframes saR{from{opacity:0;transform:translateX(70px)}}
@keyframes saRise{from{opacity:0;transform:translateY(60px)}}
@keyframes saFloat{50%{transform:translateY(-10px)}}

@media(max-width:1000px){
  .sa-hero{flex-direction:column;gap:56px;padding:70px 0 60px}
  .sa-hero-text{max-width:100%}
  .sa-panel{width:100%;max-width:420px}
  .sa-float{right:-6px}
  .sa-grid{grid-template-columns:repeat(2,minmax(0,1fr))}
}
@media(max-width:600px){
  .sa h1,.sa-sec h2{font-size:30px}
  .sa-grid{grid-template-columns:1fr}
  .sa-sec{padding-bottom:70px}.sa-sub{margin-bottom:40px}
  .sa-cta{padding:30px 22px;margin-bottom:70px}.sa-cta h3{font-size:22px}
}
@media(prefers-reduced-motion:reduce){
  .sa *,.sa *::before,.sa *::after{animation:none!important;transition:none!important}
  .sa-grid .sa-card{opacity:1}
}
`;

const FeatureCard = ({ item, index }: { item: (typeof features)[number]; index: number }) => {
  const ref = useRef<HTMLDivElement | null>(null);

  const handleMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    el.style.setProperty("--mx", x + "px");
    el.style.setProperty("--my", y + "px");
    el.style.setProperty("--ry", ((x / r.width - 0.5) * 8).toFixed(2) + "deg");
    el.style.setProperty("--rx", ((0.5 - y / r.height) * 8).toFixed(2) + "deg");
  };
  const handleLeave = () => {
    ref.current?.style.setProperty("--rx", "0deg");
    ref.current?.style.setProperty("--ry", "0deg");
  };

  return (
    <div className="sa-card" ref={ref} style={{ ["--i" as any]: index }} onMouseMove={handleMove} onMouseLeave={handleLeave}>
      <div className="sa-top" />
      <div className="sa-glow" />
      <span className="sa-num">{String(index + 1).padStart(2, "0")}</span>
      <div className="sa-icon">
        <svg viewBox="0 0 24 24">{item.icon}</svg>
      </div>
      <h3>{item.title}</h3>
      <p>{item.text}</p>
    </div>
  );
};

const StaffAugmentation = () => {
  const history = useHistory();
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { threshold: 0.12 }
    );
    if (gridRef.current) observer.observe(gridRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <Navbar />
      <div className="sa">
        <style>{STYLES}</style>

        <div className="sa-wrap">
          {/* HERO */}
          <div className="sa-hero">
            <div className="sa-hero-text">
              <div className="sa-chip">On-Demand Developers</div>
              <h1>Staff Augmentation Services</h1>
              <div className="sa-line"><i /><i /></div>
              <p className="sa-desc">
                At <b>ATUM Information Technologies</b>, we specialize in delivering highly qualified, on-demand
                developers to power your projects forward. Whether your needs are short term, long term, or even
                hourly, our vetted experts integrate seamlessly into your existing teams and workflows.
              </p>
              <button className="sa-btn" onClick={() => history.push("/contact")}>
                Contact Us <span>→</span>
              </button>
            </div>

            <div className="sa-panel">
              <div className="sa-panel-box">
                <h3>Choose Your Engagement</h3>
                {models.map((m, i) => (
                  <div className="sa-model" key={m.name}>
                    <em>{i + 1}</em>
                    <div>
                      <b>{m.name}</b>
                      <span>{m.note}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="sa-float">
                <span className="dot">✓</span>
                <div>Backup Resource<b>Zero Downtime</b></div>
              </div>
            </div>
          </div>

          {/* FEATURES */}
          <div className="sa-sec">
            <div className="sa-chip">Why ATUM</div>
            <h2>Why Choose Our Staff Augmentation?</h2>
            <div className="sa-sub">
              <i />
              <p>Vetted experts, reviewed work and a transparent price.</p>
              <i />
            </div>
            <div className={"sa-grid" + (inView ? " in" : "")} ref={gridRef}>
              {features.map((f, i) => (
                <FeatureCard key={f.title} item={f} index={i} />
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="sa-cta">
            <div>
              <h3>Ready to move faster?</h3>
              <p>
                Get in touch today to explore our <b>Staff Augmentation Services</b> and discover how{" "}
                <b>ATUM Information Technologies</b> can help you move faster, smarter, and with the right expertise.
              </p>
            </div>
            <button className="sa-btn solid" onClick={() => history.push("/contact")}>
              Contact Us <span>→</span>
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default StaffAugmentation;